import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './Page5.css';

const API = 'https://kidr78home.duckdns.org/api';
const AUTH = { headers: { Authorization: 'kSuIlUmNaTiIeO5' } };

const briToPct = (bri) => Math.round((bri / 254) * 100);
const pctToBri = (pct) => Math.round((pct / 100) * 254) || 1;
const ctToSlider = (ct) => Math.round(((500 - ct) / (500 - 153)) * 100);
const sliderToCt = (val) => Math.round(500 - (val / 100) * (500 - 153));

const ROOM_TYPES = ['Room', 'Zone'];
const ROOM_ICONS = {
  kitchen: '🍳', 'living room': '🛋️', bedroom: '🛏️', office: '💻',
  entrance: '🚪', "maria's room": '🧸', studio: '🎨', monitors: '🖥️',
  downstairs: '🏠', 'living space': '🛋️', worklights: '💡',
};
const getRoomIcon = (name) => ROOM_ICONS[name.toLowerCase()] || '💡';

function LightsPage() {
  const [lights, setLights] = useState({});
  const [groups, setGroups] = useState({});
  const [scenes, setScenes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showScenes, setShowScenes] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const briTimers = useRef({});

  const showToast = useCallback((msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(''), 3000);
  }, []);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [lRes, gRes, sRes] = await Promise.all([
        axios.get(`${API}/lamps/status`, AUTH),
        axios.get(`${API}/lamps/groups`, AUTH),
        axios.get(`${API}/lamps/scenes`, AUTH),
      ]);
      setLights(lRes.data.lights || {});
      setGroups(gRes.data.groups || {});
      setScenes(sRes.data.scenes || {});
      setError('');
    } catch (e) {
      setError('Failed to connect to Hue bridge');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleGroup = async (gid) => {
    const grp = groups[gid];
    const newState = !grp.any_on;
    setActionLoading(p => ({ ...p, [`g${gid}`]: true }));
    try {
      await axios.put(`${API}/lamps/group/${gid}`, { on: newState }, AUTH);
      setGroups(prev => ({
        ...prev,
        [gid]: { ...prev[gid], any_on: newState, all_on: newState, action: { ...prev[gid].action, on: newState } }
      }));
      setLights(prev => {
        const next = { ...prev };
        grp.lights.forEach(lid => {
          if (next[lid]) next[lid] = { ...next[lid], on: newState };
        });
        return next;
      });
      showToast(`${grp.name} turned ${newState ? 'on' : 'off'}`);
    } catch (e) {
      showToast(`Failed to toggle ${grp.name}`, true);
    } finally {
      setActionLoading(p => ({ ...p, [`g${gid}`]: false }));
    }
  };

  const toggleLight = async (lid) => {
    const light = lights[lid];
    const newState = !light.on;
    setActionLoading(p => ({ ...p, [`l${lid}`]: true }));
    try {
      await axios.put(`${API}/lamps/light/${lid}`, { on: newState }, AUTH);
      setLights(prev => ({ ...prev, [lid]: { ...prev[lid], on: newState } }));
    } catch (e) {
      showToast(`Failed to toggle ${light.name}`, true);
    } finally {
      setActionLoading(p => ({ ...p, [`l${lid}`]: false }));
    }
  };

  const setLightBrightness = (lid, pct) => {
    const bri = pctToBri(pct);
    setLights(prev => ({ ...prev, [lid]: { ...prev[lid], bri, on: true } }));
    clearTimeout(briTimers.current[`bri_${lid}`]);
    briTimers.current[`bri_${lid}`] = setTimeout(async () => {
      try {
        await axios.put(`${API}/lamps/light/${lid}`, { bri, on: true }, AUTH);
      } catch (e) {
        showToast(`Failed to set brightness`, true);
      }
    }, 300);
  };

  const setGroupBrightness = (gid, pct) => {
    const bri = pctToBri(pct);
    setGroups(prev => ({
      ...prev,
      [gid]: { ...prev[gid], action: { ...prev[gid].action, bri } }
    }));
    clearTimeout(briTimers.current[`gbri_${gid}`]);
    briTimers.current[`gbri_${gid}`] = setTimeout(async () => {
      try {
        await axios.put(`${API}/lamps/group/${gid}`, { bri, on: true }, AUTH);
        setGroups(prev => ({
          ...prev,
          [gid]: { ...prev[gid], any_on: true, action: { ...prev[gid].action, on: true, bri } }
        }));
      } catch (e) {
        showToast(`Failed to set brightness`, true);
      }
    }, 300);
  };

  const setLightCt = (lid, sliderVal) => {
    const ct = sliderToCt(sliderVal);
    setLights(prev => ({ ...prev, [lid]: { ...prev[lid], ct } }));
    clearTimeout(briTimers.current[`ct_${lid}`]);
    briTimers.current[`ct_${lid}`] = setTimeout(async () => {
      try {
        await axios.put(`${API}/lamps/light/${lid}`, { ct }, AUTH);
      } catch (e) {
        showToast(`Failed to set color temp`, true);
      }
    }, 300);
  };

  const applyScene = async (sceneId, gid, sceneName) => {
    setActionLoading(p => ({ ...p, [`s${sceneId}`]: true }));
    try {
      await axios.put(`${API}/lamps/group/${gid}`, { scene: sceneId }, AUTH);
      showToast(`Scene "${sceneName}" applied`);
      setTimeout(() => fetchAll(true), 500);
    } catch (e) {
      showToast(`Failed to apply scene`, true);
    } finally {
      setActionLoading(p => ({ ...p, [`s${sceneId}`]: false }));
    }
  };

  const getScenesForGroup = (gid) => {
    return Object.entries(scenes)
      .filter(([, s]) => s.group === gid && s.type === 'GroupScene')
      .sort((a, b) => a[1].name.localeCompare(b[1].name));
  };

  const roomGroups = Object.entries(groups)
    .filter(([, g]) => ROOM_TYPES.includes(g.type))
    .sort((a, b) => a[1].name.localeCompare(b[1].name));

  // Selected group data
  const selGrp = selectedGroup ? groups[selectedGroup] : null;
  const selLights = selGrp ? selGrp.lights.map(lid => ({ lid, ...lights[lid] })).filter(l => l.name) : [];
  const selScenes = selectedGroup ? getScenesForGroup(selectedGroup) : [];

  if (loading) {
    return (
      <div className="lights-page">
        <div className="lights-loading">
          <div className="loading-bulb">💡</div>
          <p>Connecting to Hue Bridge...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lights-page">
        <div className="lights-error">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={() => fetchAll()} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="lights-page">
      <div className="lights-header">
        <h1 className="lights-title">💡 Smart Lighting</h1>
        <button className="refresh-btn-header" onClick={() => fetchAll(true)} title="Refresh">🔄</button>
      </div>

      {/* Room Selector Buttons */}
      <div className="room-selector">
        {roomGroups.map(([gid, grp]) => {
          const isOn = grp.any_on;
          const isSelected = selectedGroup === gid;
          const onCount = grp.lights.filter(lid => lights[lid]?.on).length;
          return (
            <button
              key={gid}
              className={`room-btn ${isOn ? 'on' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => setSelectedGroup(isSelected ? null : gid)}
            >
              <span className="room-btn-icon">{getRoomIcon(grp.name)}</span>
              <span className="room-btn-name">{grp.name}</span>
              {isOn && <span className="room-btn-count">{onCount}</span>}
            </button>
          );
        })}
      </div>

      {/* Selected Room Card */}
      {selGrp && (
        <div className="room-detail">
          {/* Room Header with master toggle */}
          <div className="detail-header">
            <div className="detail-header-left">
              <span className="detail-icon">{getRoomIcon(selGrp.name)}</span>
              <div>
                <h2 className="detail-name">{selGrp.name}</h2>
                <span className="detail-count">
                  {selGrp.lights.filter(lid => lights[lid]?.on).length}/{selGrp.lights.length} lights on
                </span>
              </div>
            </div>
            <button
              className={`room-toggle ${selGrp.any_on ? 'on' : 'off'}`}
              onClick={() => toggleGroup(selectedGroup)}
              disabled={!!actionLoading[`g${selectedGroup}`]}
              aria-label={`Toggle ${selGrp.name}`}
            >
              <span className="toggle-knob" />
            </button>
          </div>

          {/* Group Brightness */}
          {selGrp.any_on && (
            <div className="detail-brightness">
              <span className="bri-label">🔅</span>
              <input
                type="range" min="1" max="100"
                value={briToPct(selGrp.action.bri)}
                onChange={(e) => setGroupBrightness(selectedGroup, parseInt(e.target.value))}
                className="bri-slider group-slider"
                aria-label="Group brightness"
              />
              <span className="bri-label">🔆</span>
              <span className="bri-pct">{briToPct(selGrp.action.bri)}%</span>
            </div>
          )}

          {/* Scenes Toggle */}
          {selScenes.length > 0 && (
            <div className="detail-scenes">
              <button
                className={`scenes-btn ${showScenes ? 'active' : ''}`}
                onClick={() => setShowScenes(!showScenes)}
              >
                🎨 Scenes ({selScenes.length})
              </button>
              {showScenes && (
                <div className="scene-chips">
                  {selScenes.map(([sid, scene]) => (
                    <button
                      key={sid}
                      className={`scene-chip ${actionLoading[`s${sid}`] ? 'loading' : ''}`}
                      onClick={() => applyScene(sid, selectedGroup, scene.name)}
                      disabled={!!actionLoading[`s${sid}`]}
                    >
                      {scene.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Individual Lights */}
          <div className="detail-lights">
            {selLights.map(light => (
              <div key={light.lid} className={`light-row ${light.on ? 'light-on' : ''} ${!light.reachable ? 'unreachable' : ''}`}>
                <div className="light-top">
                  <span className={`light-dot ${light.on ? 'on' : ''}`} />
                  <span className="light-name">{light.name}</span>
                  {!light.reachable && <span className="offline-badge">offline</span>}
                  <button
                    className={`light-toggle ${light.on ? 'on' : 'off'}`}
                    onClick={() => toggleLight(light.lid)}
                    disabled={!light.reachable || !!actionLoading[`l${light.lid}`]}
                    aria-label={`Toggle ${light.name}`}
                  >
                    <span className="toggle-knob-sm" />
                  </button>
                </div>
                {light.on && light.reachable && (
                  <div className="light-sliders">
                    <div className="slider-row">
                      <span className="slider-label">Brightness</span>
                      <input
                        type="range" min="1" max="100"
                        value={briToPct(light.bri)}
                        onChange={(e) => setLightBrightness(light.lid, parseInt(e.target.value))}
                        className="bri-slider"
                        aria-label={`${light.name} brightness`}
                      />
                      <span className="slider-val">{briToPct(light.bri)}%</span>
                    </div>
                    {light.ct !== undefined && (
                      <div className="slider-row">
                        <span className="slider-label">Temp</span>
                        <input
                          type="range" min="0" max="100"
                          value={ctToSlider(light.ct)}
                          onChange={(e) => setLightCt(light.lid, parseInt(e.target.value))}
                          className="ct-slider"
                          aria-label={`${light.name} color temperature`}
                        />
                        <span className="slider-val">{ctToSlider(light.ct) < 33 ? '🔥' : ctToSlider(light.ct) > 66 ? '❄️' : '☀️'}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when nothing selected */}
      {!selectedGroup && (
        <div className="empty-state">
          <span className="empty-icon">👆</span>
          <p>Tap a room to control its lights</p>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`lights-toast ${toast.isError ? 'error' : 'success'}`}>
          <span>{toast.isError ? '❌' : '✅'}</span>
          <span>{toast.msg}</span>
        </div>
      )}
    </div>
  );
}

export default LightsPage;
